import { useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from './client'

export type Role =
  | 'super_admin'
  | 'founder'
  | 'admin'
  | 'doctor'
  | 'assistant'
  | 'call_center'
  | 'quality'
  | 'marketing'
  | 'integrator'

export const ALL_ROLES: Role[] = ['super_admin', 'founder', 'admin', 'doctor', 'assistant', 'call_center', 'quality', 'marketing', 'integrator']

export interface Me {
  kind: string
  user_id?: string
  name: string
  email?: string
  roles: string[]
  permissions: string[]
  totp_enabled: boolean
  language?: string
}

export interface User {
  id: string
  email: string
  full_name: string
  status: 'active' | 'invited' | 'disabled' | string
  roles: string[]
  totp_enabled: boolean
  language: string
  last_login_at?: string
  created_at: string
  /** Number of per-user permission overrides (users.access_set). */
  overrides_count?: number
  has_overrides?: boolean
}

export interface LoginResult {
  token: string
  expires_at: string
  user: User
}

export const authApi = {
  login: (email: string, password: string, totp?: string) =>
    request<LoginResult>('POST', '/v1/auth/login', { email, password, ...(totp ? { totp } : {}) }, { silent401: true }),
  logout: () => request<{ ok: boolean }>('POST', '/v1/auth/logout', {}, { silent401: true }),
  me: () => request<Me>('GET', '/v1/auth/me', undefined, { silent401: true }),
  acceptInvite: (token: string, password: string) =>
    request<User>('POST', '/v1/auth/invite/accept', { token, password }, { silent401: true }),
}

export const meKey = ['auth', 'me'] as const

export function useMe() {
  return useQuery({ queryKey: meKey, queryFn: authApi.me, staleTime: 60_000, retry: false })
}

export function useInvalidateMe() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: meKey })
}

export function hasRole(me: Me | undefined, ...roles: string[]): boolean {
  if (!me) return false
  return roles.some((r) => me.roles.includes(r))
}

export function hasPerm(me: Me | undefined, perm: string): boolean {
  if (!me) return false
  return perm === 'authenticated' || me.permissions.includes(perm)
}
